import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';

export default function EndOfDayStatements() {
  const { companyId } = useAuth();
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [regenLoadingId, setRegenLoadingId] = useState(null);

  const currency = (val) => `RM ${Number(val || 0).toFixed(2)}`;

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const loadStatements = async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const ref = collection(db, 'companies', companyId, 'cashSessions');
      const q = query(ref, orderBy('startedAt', 'desc'));
      const snap = await getDocs(q);
      const companySnap = await getDoc(doc(db, 'companies', companyId, 'companyProfile', 'info'));
      const companyName = companySnap.exists() ? companySnap.data().companyName || '' : '';
      const rows = snap.docs.map((d) => ({ id: d.id, companyName, ...d.data() }));
      const filtered = rows.filter((row) => {
        if (!selectedDate) return true;
        const started = row.startedAt ? new Date(row.startedAt) : null;
        if (!started) return false;
        const key = started.toISOString().split('T')[0];
        return key === selectedDate;
      });
      setStatements(filtered);
    } catch (err) {
      console.error('Failed to load end of day statements:', err);
      setError('Unable to load end of day statements right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatements();
  }, [companyId, selectedDate]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  }, []);

  const aggregateSales = (orders) => {
    const productMap = {};
    const bundleMap = {};

    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const qty = Number(item.qty ?? item.quantity ?? 1) || 0;
        const price = Number(item.price || 0);
        const lineTotal = qty * price;
        const type = item.type || 'product';

        if (type === 'bundle') {
          if (!bundleMap[item.id]) {
            bundleMap[item.id] = { id: item.id, name: item.name || 'Bundle', qty: 0, total: 0 };
          }
          bundleMap[item.id].qty += qty;
          bundleMap[item.id].total += lineTotal;
        } else {
          if (!productMap[item.id]) {
            productMap[item.id] = { id: item.id, name: item.name || 'Product', qty: 0, total: 0, variants: {} };
          }
          productMap[item.id].qty += qty;
          productMap[item.id].total += lineTotal;

          if (item.variantId || item.variantName) {
            const variantKey = item.variantId || item.variantName;
            if (!productMap[item.id].variants[variantKey]) {
              productMap[item.id].variants[variantKey] = {
                id: item.variantId || variantKey,
                name: item.variantName || 'Variant',
                qty: 0,
                total: 0
              };
            }
            productMap[item.id].variants[variantKey].qty += qty;
            productMap[item.id].variants[variantKey].total += lineTotal;
          }
        }
      });
    });

    const products = Object.values(productMap).map((p) => ({
      ...p,
      variants: Object.values(p.variants || {})
    }));
    const bundles = Object.values(bundleMap);
    return { products, bundles };
  };

  const buildLineRows = (s) => {
    const rows = [];
    (s.productSales || []).forEach((p) => {
      rows.push(['Product', p.name, '—', p.qty || 0, Number(p.total || 0)]);
      (p.variants || []).forEach((v) => {
        rows.push(['Variant', `${p.name} - ${v.name}`, v.name || '-', v.qty || 0, Number(v.total || 0)]);
      });
    });
    (s.bundleSales || []).forEach((b) => {
      rows.push(['Bundle', b.name, '—', b.qty || 0, Number(b.total || 0)]);
    });
    return rows;
  };

  const exportCsv = (s) => {
    const rows = buildLineRows(s);
    const lines = [];
    lines.push(`Company,${s.companyName || ''}`);
    lines.push(`Date,${s.dateKey || ''}`);
    lines.push(`Started,${formatDateTime(s.startedAt)}`);
    lines.push(`Ended,${formatDateTime(s.endedAt)}`);
    lines.push(`Opening Cash,${s.openingTotal}`);
    lines.push(`Closing Cash,${s.closingTotal}`);
    lines.push(`Cash Sales,${s.cashSalesTotal}`);
    lines.push(`Card Sales,${s.cardSalesTotal || 0}`);
    lines.push(`E-Wallet Sales,${s.ewalletSalesTotal || 0}`);
    lines.push(`Voided Orders,${s.voidCount || 0}`);
    lines.push(`Variance,${s.cashVariance}`);
    lines.push('');
    lines.push('Type,Name,Variant,Qty,Total');
    rows.forEach((r) => {
      lines.push(r.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `end-of-day_${s.dateKey || 'session'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPdf = (s) => {
    const docPdf = new jsPDF();
    const startY = 14;
    docPdf.setFontSize(14);
    docPdf.text('End of Day Statement', 14, startY);
    docPdf.setFontSize(11);
    if (s.companyName) {
      docPdf.text(s.companyName, 14, startY + 7);
    }

    const summaryRows = [
      ['Date', s.dateKey || '-'],
      ['Started', formatDateTime(s.startedAt)],
      ['Ended', formatDateTime(s.endedAt)],
      ['Opening Cash', currency(s.openingTotal)],
      ['Closing Cash', currency(s.closingTotal)],
      ['Cash Sales', currency(s.cashSalesTotal)],
      ['Card Sales', currency(s.cardSalesTotal || 0)],
      ['E-Wallet Sales', currency(s.ewalletSalesTotal || 0)],
      ['Voided Orders', s.voidCount || 0],
      ['Variance', currency(s.cashVariance)],
    ];

    autoTable(docPdf, {
      startY: startY + (s.companyName ? 10 : 6),
      head: [['Field', 'Value']],
      body: summaryRows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [76, 29, 149] }
    });

    const rows = buildLineRows(s).map((r) => [
      r[0],
      r[1],
      r[2],
      r[3],
      currency(r[4])
    ]);

    if (rows.length > 0) {
      autoTable(docPdf, {
        startY: docPdf.lastAutoTable.finalY + 8,
        head: [['Type', 'Name', 'Variant', 'Qty', 'Total']],
        body: rows,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [76, 29, 149] }
      });
    }

    docPdf.save(`end-of-day_${s.dateKey || 'session'}.pdf`);
  };

  const regenerateStatement = async (s) => {
    if (!companyId || !s?.id) return;
    const sessionDate = s.dateKey || (s.startedAt ? new Date(s.startedAt).toISOString().split('T')[0] : '');
    if (!sessionDate) return;
    if (!window.confirm('Regenerate this end-of-day statement? This will overwrite the existing totals.')) return;
    try {
      setRegenLoadingId(s.id);
      const start = new Date(`${sessionDate}T00:00:00.000Z`);
      const end = new Date(`${sessionDate}T23:59:59.999Z`);

      const ordersRef = collection(db, 'companies', companyId, 'orders');
      const ordersQuery = query(
        ordersRef,
        orderBy('timestamp'),
        where('timestamp', '>=', start.toISOString()),
        where('timestamp', '<=', end.toISOString())
      );
      const snap = await getDocs(ordersQuery);
      const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const normalizePayment = (pm = '') => pm.toString().toLowerCase();
      const normalizeStatus = (status = '') => status.toString().toLowerCase();

      const nonVoidOrders = orders.filter((o) => normalizeStatus(o.paymentStatus) !== 'void');
      const voidCount = orders.length - nonVoidOrders.length;

      const cashOrders = nonVoidOrders.filter((o) => normalizePayment(o.paymentMethod) === 'cash');
      const cardOrders = nonVoidOrders.filter((o) => {
        const m = normalizePayment(o.paymentMethod);
        return m.includes('card') || m.includes('debit') || m.includes('credit');
      });
      const ewalletOrders = nonVoidOrders.filter((o) => normalizePayment(o.paymentMethod).includes('wallet'));

      const cashSalesTotal = cashOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
      const cardSalesTotal = cardOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
      const ewalletSalesTotal = ewalletOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

      const { products, bundles } = aggregateSales(nonVoidOrders);
      const variance = Number(s.closingTotal || 0) - (Number(s.openingTotal || 0) + cashSalesTotal);

      const sessionRef = doc(db, 'companies', companyId, 'cashSessions', s.id);
      await updateDoc(sessionRef, {
        cashSalesTotal,
        cardSalesTotal,
        ewalletSalesTotal,
        voidCount,
        productSales: products,
        bundleSales: bundles,
        cashVariance: variance
      });

      await loadStatements();
    } catch (err) {
      console.error('Failed to regenerate statement:', err);
      setError('Failed to regenerate statement.');
    } finally {
      setRegenLoadingId(null);
    }
  };

  const renderLineItems = (title, rows, includeVariants = false) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        <div className="bg-[#111] border border-gray-800 rounded-lg divide-y divide-gray-800">
          {rows.map((row) => (
            <div key={row.id} className="p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-200">{row.name}</span>
                <span className="text-gray-200">Qty: {row.qty} · {currency(row.total)}</span>
              </div>
              {includeVariants && row.variants?.length > 0 && (
                <div className="mt-2 ml-3 space-y-1">
                  {row.variants.map((variant) => (
                    <div key={variant.id} className="flex justify-between text-xs text-gray-400">
                      <span>{variant.name}</span>
                      <span>Qty: {variant.qty} · {currency(variant.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 text-white">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold">End of Day Statements</h1>
          <p className="text-sm text-gray-400">Cash sessions with payment breakdowns and itemized sales.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Back
          </button>
          <label className="text-sm text-gray-300">
            Date:
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="ml-2 bg-[#0f0f0f] border border-gray-700 rounded px-3 py-2 text-white text-sm"
            />
          </label>
        </div>
      </div>

      {error && <p className="text-red-400 mb-3 text-sm">{error}</p>}

      {statements.length === 0 && !loading && (
        <div className="bg-[#0f0f0f] border border-gray-800 rounded-lg p-4 text-sm text-gray-300">
          No end-of-day statements yet. Complete Start of Day and End of Day to generate one.
        </div>
      )}

      <div className="space-y-3">
        {statements.map((s) => {
          const isOpen = selectedId === s.id;
          return (
            <div key={s.id} className="bg-[#0f0f0f] border border-gray-800 rounded-lg p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-400">{s.dateKey || ''}</p>
                  <p className="text-lg font-semibold">Variance: {currency(s.cashVariance)}</p>
                  <p className="text-xs text-gray-400">
                    Started: {formatDateTime(s.startedAt)} · Ended: {formatDateTime(s.endedAt)}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">Opening Cash</p>
                    <p className="text-white font-semibold">{currency(s.openingTotal)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">Closing Cash</p>
                    <p className="text-white font-semibold">{currency(s.closingTotal)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">Cash Sales</p>
                    <p className="text-white font-semibold">{currency(s.cashSalesTotal)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">Card</p>
                    <p className="text-white font-semibold">{currency(s.cardSalesTotal || 0)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">E-Wallet</p>
                    <p className="text-white font-semibold">{currency(s.ewalletSalesTotal || 0)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400">Voided</p>
                    <p className="text-white font-semibold">{s.voidCount || 0}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedId(isOpen ? null : s.id)}
                  className="self-start md:self-center bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm"
                >
                  {isOpen ? 'Hide Details' : 'View Details'}
                </button>
              </div>

              {isOpen && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">Variance</p>
                      <p className="text-white font-semibold">{currency(s.cashVariance)}</p>
                    </div>
                    <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">Voided Orders</p>
                      <p className="text-white font-semibold">{s.voidCount || 0}</p>
                    </div>
                    <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                      <p className="text-gray-400">Date</p>
                      <p className="text-white font-semibold">{s.dateKey || '-'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportPdf(s)}
                      className="bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded-lg text-sm font-semibold"
                    >
                      Export PDF
                    </button>
                    <button
                      onClick={() => exportCsv(s)}
                      className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm font-semibold"
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={() => regenerateStatement(s)}
                      className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                      disabled={!!regenLoadingId && regenLoadingId === s.id}
                    >
                      {regenLoadingId === s.id ? 'Regenerating...' : 'Regenerate (Overwrite)'}
                    </button>
                  </div>
                  {renderLineItems('Products Sold (All)', s.productSales, true)}
                  {renderLineItems('Bundles Sold (All)', s.bundleSales)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
